/**
 * @file `DashboardPanel` — the cross-source operator home (umbrella
 *       #883, part #890). The new meaning of "Brain": a unified
 *       Needs-you inbox, a Sources signal board, and a cross-source
 *       Ask, all synthesized over the live admin sources.
 * @module @labcraft/admin/ui/dashboard/dashboard-panel
 *
 * Renders at `/` for a signed-in operator (see `home_page.tsx`).
 * ACCOUNT/COURSES/SECRETS are wired to their live hooks; HYPERVISOR,
 * EMAIL and BILLING render honest "not connected yet" tiles
 * until their sources land (HYPERVISOR upgrades in #891; EMAIL is
 * deferred; BILLING is the standing blind spot).
 * Posture pills are a later retrofit (#897) — v1 ships a chevron.
 */

import { useMemo, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { ChevronRightIcon } from '@labcraft/ui/icons'
import { PosturePill } from '../kernel/posture_pill.tsx'
import {
  useCoursesList,
  useHypervisorMachines,
  useLearners,
  useSecretsList,
} from '../hooks/index.ts'
import {
  SourceContribution,
  type SourceContributionData,
} from '../sources/contract.ts'
import { DashboardSources } from './contributions.ts'
import { HypervisorContribution } from '../hypervisor/contribution.ts'
import { DashboardAsk, type AskAnswer } from './ask.ts'

/** Pinned one-click questions shown above the Ask bar. */
const PINNED = ['what needs me?', "how's the funnel?", "what's connected?"]

/**
 * Format a fetch timestamp as a terse relative "last sync" string.
 *
 * @param updatedAt - The freshest query's `dataUpdatedAt` (ms), or 0.
 * @param now - Current time in ms.
 * @returns A short relative string, or `—` when never fetched.
 */
function lastSync(updatedAt: number, now: number): string {
  if (updatedAt <= 0) return '—'
  const secs = Math.max(0, Math.round((now - updatedAt) / 1000))
  if (secs < 60) return `${secs}s ago`
  return `${Math.round(secs / 60)}m ago`
}

/** The cross-source operator home. */
export function DashboardPanel(): JSX.Element {
  const navigate = useNavigate()
  const learners = useLearners({})
  // The dashboard reads one page for its at-a-glance counts (the daemon's
  // default page size when no `limit` is passed). Memoized so the array
  // keeps a stable identity for the dependent memos.
  const learnerRows = useMemo(
    () => learners.data?.learners ?? [],
    [learners.data],
  )
  const courses = useCoursesList()
  const secrets = useSecretsList()
  const hypervisor = useHypervisorMachines()

  const [query, setQuery] = useState('')
  const [asked, setAsked] = useState('')
  const [answer, setAnswer] = useState<AskAnswer | null>(null)

  const contributions = useMemo<SourceContributionData[]>(() => {
    const now = Date.now()
    return [
      DashboardSources.account(learnerRows),
      DashboardSources.courses(courses.data ?? []),
      DashboardSources.secrets(secrets.data?.entries ?? [], now),
      HypervisorContribution.build(
        hypervisor.data ?? { connected: false, machines: [] },
      ),
      DashboardSources.notConnected(
        'EMAIL',
        'Email',
        '/settings',
        'delivery tracking — not connected yet',
      ),
      DashboardSources.notConnected(
        'BILLING',
        'Billing',
        '/settings',
        'revenue + churn — not connected yet',
      ),
    ]
  }, [learnerRows, courses.data, secrets.data, hypervisor.data])

  const needs = useMemo(
    () => SourceContribution.aggregate(contributions.map(c => c.items)),
    [contributions],
  )
  const tiles = contributions.map(c => c.tile)
  const { live, total } = SourceContribution.liveCount(tiles)

  const updatedAt = Math.max(
    learners.dataUpdatedAt,
    courses.dataUpdatedAt,
    secrets.dataUpdatedAt,
    hypervisor.dataUpdatedAt,
  )

  const localAnswer = (q: string): AskAnswer | null => {
    const awaiting = learnerRows.filter(l => l.status === 'inactive').length
    return DashboardAsk.resolve(q, {
      learnersTotal: learnerRows.length,
      awaiting,
      coursesTotal: courses.data?.length ?? 0,
      coursesEnabled:
        courses.data?.filter(c => c.enabledAt !== null).length ?? 0,
      secretsStale: tiles.find(t => t.source === 'SECRETS')?.needsYouCount ?? 0,
      needsCount: needs.length,
      liveCount: live,
      sourcesTotal: total,
    })
  }

  const runAsk = (q: string): void => {
    if (q.trim().length === 0) return
    setAsked(q)
    // Answer honestly from the live boards we already hold.
    setAnswer(localAnswer(q))
  }

  return (
    <div className="panel panel--no-subbar dashboard">
      <div className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Dashboard</h1>
            <span className="panel-head__count">
              {live} of {total} sources live · last sync{' '}
              {lastSync(updatedAt, Date.now())}
            </span>
          </div>
          <div className="panel-head__sub">
            // cross-source operator console — what needs you, across the whole
            operation
          </div>
        </div>
      </div>

      <div className="dash">
        <div className="dash-ask">
          <div className="pinned">
            {PINNED.map(q => (
              <button
                key={q}
                className="pinq"
                onClick={() => {
                  setQuery(q)
                  runAsk(q)
                }}>
                <span className="dia">◇</span> {q}
              </button>
            ))}
          </div>
          <div className="askbar">
            <span className="gt">❯</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') runAsk(query)
              }}
              placeholder="ask across the business…"
              aria-label="Ask across sources"
            />
            <span className="go">
              <span className="kbd">↵</span> ask
            </span>
          </div>
          {answer && (
            <div className="answer">
              <div className="answer__q">
                <span className="gt">❯</span> {asked}
              </div>
              <div className="answer__cols">
                <div className="answer__main">
                  <p className="answer__body">
                    {answer.body.map((p, i) => (
                      <span key={i}>
                        {p.text}
                        {p.cite !== undefined && (
                          <span className="cite">{p.cite}</span>
                        )}
                      </span>
                    ))}
                  </p>
                </div>
                <div className="answer__side">
                  {answer.cites.length > 0 && (
                    <div className="cites">
                      <div className="cites__h">sources</div>
                      {answer.cites.map(c => (
                        <div key={c.n} className="crow">
                          <span className="n">{c.n}</span>
                          <span>{c.src}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="gapb">
                    <span className="gl">no data</span>
                    <p>{answer.gap}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dash-grid">
          <section className="bcard" aria-label="Needs you">
            <div className="bcard__h">
              <h3>Needs you — {needs.length}</h3>
              <span className="bcard__meta">across all sources</span>
            </div>
            <div className="bcard__scroll">
              {needs.length === 0 ? (
                <div className="empty-line">// nothing needs you.</div>
              ) : (
                needs.map(it => (
                  <div
                    key={it.id}
                    className="dnh-row"
                    onClick={() => navigate(it.route)}
                    role="button"
                    tabIndex={0}>
                    <span className="src-tag">{it.source}</span>
                    <div className="ttl">
                      {it.title}
                      <small>{it.sub}</small>
                    </div>
                    {it.posture ? (
                      <PosturePill posture={it.posture} />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="bcard" aria-label="Sources">
            <div className="bcard__h">
              <h3>Sources</h3>
              <span className="bcard__meta">
                {live}/{total} live
              </span>
            </div>
            <div className="bcard__scroll">
              {tiles.map(s => (
                <div
                  key={s.source}
                  className={`src-tile ${s.health === 'live' ? '' : 'off'}`}
                  onClick={() => s.health === 'live' && navigate(s.route)}
                  role={s.health === 'live' ? 'button' : undefined}
                  tabIndex={s.health === 'live' ? 0 : undefined}>
                  <span className="src-tile__name">
                    <span
                      className={`src-dot ${s.health === 'live' ? '' : 'off'}`}
                    />
                    {s.label}
                  </span>
                  {s.state === 'needs-you' ? (
                    <span className="src-tile__needs">
                      {s.needsYouCount} need you
                    </span>
                  ) : s.state === 'ok' ? (
                    <span className="src-tile__ok">ok</span>
                  ) : (
                    <span className="src-tile__off">—</span>
                  )}
                  <span className="src-tile__stat">{s.stat}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
